import type { CommandCenterResponse } from "../api/client.js";

/**
 * Command Center situations (B7 / UX-03). Every way the page can be "not just
 * ready" is named here, with what it means and the ONE next step, so the screen
 * never shows a bare dash or an unexplained empty chart.
 *
 * States decided before this page loads (handled by the sign-in gate):
 *   - not registered     -> sign-in / sign-up form
 *   - workspace pending  -> "No CoreFinOps workspace yet": create one or join with an invitation code
 * States decided here, from the Command Center response:
 */
export type NoticeId =
  | "licence_ended"
  | "licence_grace"
  | "payment_pending"
  | "demo"
  | "no_account"
  | "no_data_export"
  | "processing"
  | "incomplete"
  | "stale";

export interface Notice {
  readonly id: NoticeId;
  readonly tone: "info" | "warn" | "danger";
  readonly title: string;
  readonly body: string;
  readonly action?: { readonly label: string; readonly path: string };
}

const PENDING_PAYMENT = new Set(["pending_activation", "pending_link", "incomplete", "past_due", "unpaid", "pending"]);

/** Notices in priority order (most important first). Pure: same response, same notices. */
export function commandCenterNotices(data: Pick<CommandCenterResponse, "entitlements" | "dataQuality">): Notice[] {
  const e = data.entitlements;
  const q = data.dataQuality;
  const out: Notice[] = [];

  if (!e.isDemo && e.effectiveStatus === "inactive") {
    out.push({
      id: "licence_ended",
      tone: "danger",
      title: "Your plan has ended",
      body: "Your data is kept and you can still read it. Adding accounts, syncing and other changes are paused until a plan is active again.",
      action: { label: "Plan & billing", path: "/settings/billing" },
    });
  } else if (!e.isDemo && e.effectiveStatus === "grace") {
    out.push({
      id: "licence_grace",
      tone: "warn",
      title: "Your plan is in a grace period",
      body: `Everything keeps working${e.graceUntil ? ` until ${e.graceUntil.slice(0, 10)}` : " for now"}. After that the workspace becomes read-only. Nothing is deleted.`,
      action: { label: "Plan & billing", path: "/settings/billing" },
    });
  }
  if (!e.isDemo && PENDING_PAYMENT.has(e.status)) {
    out.push({
      id: "payment_pending",
      tone: "info",
      title: "Waiting for your payment to be confirmed",
      body: "Your plan switches on as soon as the payment or AWS Marketplace agreement is confirmed. Until then you keep the access you already have.",
      action: { label: "Plan & billing", path: "/settings/billing" },
    });
  }
  if (e.isDemo) {
    out.push({
      id: "demo",
      tone: "info",
      title: "These are sample figures",
      body: "This demo workspace uses made-up data so you can look around. Create your own workspace and connect AWS to see your real spend.",
    });
  }

  const kinds = new Set(q.missingSources.map((m) => m.kind));
  if (!e.isDemo || q.coverage.connectedAccounts === 0) {
    if (kinds.has("no_aws_account") || q.coverage.connectedAccounts === 0) {
      out.push({
        id: "no_account",
        tone: "info",
        title: "Connect an AWS account to see your spend",
        body: "Nothing is analysed yet. Connecting takes a few minutes and uses a read-only role that you create in your own AWS account.",
        action: { label: "Connect AWS", path: "/settings" },
      });
    }
  }
  if (kinds.has("no_data_export")) {
    out.push({
      id: "no_data_export",
      tone: "warn",
      title: "An account is connected, but there is no cost export yet",
      body: "Set up the Cost and Usage Report (Data Export) for the account so cost data can flow in. Until then spend and savings stay empty.",
      action: { label: "Finish AWS set-up", path: "/settings" },
    });
  }
  if (kinds.has("no_cost_data")) {
    out.push({
      id: "processing",
      tone: "info",
      title: "Your first cost data is on its way",
      body: "The export is set up. AWS delivers the first files within about a day, then CoreFinOps processes them. This page fills in by itself.",
      action: { label: "See sync status", path: "/inventory" },
    });
  }
  const gaps = q.missingSources.filter((m) => m.kind === "account_without_cost" || m.kind === "no_inventory");
  const partial = q.coverage.dayCoverage !== null && Number(q.coverage.dayCoverage) < 0.9;
  if (gaps.length > 0 || partial) {
    out.push({
      id: "incomplete",
      tone: "warn",
      title: "Some data is missing, so figures may be low",
      body: partial
        ? `Only ${q.coverage.daysWithData} of ${q.coverage.daysExpected} expected days have cost data. Totals cover those days only.`
        : "At least one connected account has no cost data or resources yet. Totals cover the accounts that do.",
      action: { label: "Check connections", path: "/settings" },
    });
  }
  if (kinds.has("cost_data_stale")) {
    out.push({
      id: "stale",
      tone: "warn",
      title: "Cost data has not refreshed recently",
      body: "The numbers below may be out of date. Check that the Data Export is still delivering and the access role still works.",
      action: { label: "See sync status", path: "/inventory" },
    });
  }
  return out;
}
